import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatValue } from "@/lib/api";
import DmeTag from "@/components/DmeTag";
import { SentimentTag, RealTradesTag } from "@/components/SignalTags";
import PlayerBadge from "@/components/PlayerBadge";
import PlayerHeadshot, { TeamPill, PosRank } from "@/components/PlayerHeadshot";
import { Loader2, Search, X, Activity, TrendingUp, ShieldCheck, Cpu } from "lucide-react";

const FORMATS = [
  { id: "1qb", label: "1QB" },
  { id: "superflex", label: "Superflex" },
  { id: "tep", label: "TE Premium" },
];
const POSITIONS = ["ALL", "QB", "RB", "WR", "TE"];

export default function DmeValues() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialPos = (params.get("pos") || "ALL").toUpperCase();
  const [fmt, setFmt] = useState(localStorage.getItem("dme_values_fmt") || "superflex");
  const [pos, setPos] = useState(POSITIONS.includes(initialPos) ? initialPos : "ALL");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => { localStorage.setItem("dme_values_fmt", fmt); }, [fmt]);

  useEffect(() => {
    setLoading(true);
    api.get("/dme-values", { params: { format: fmt, position: pos, q: debounced || undefined, limit: 500 } })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [fmt, pos, debounced]);

  const rows = data?.players || [];

  return (
    <div className="space-y-6 fade-up" data-testid="dme-values-page">
      <header>
        <div className="text-[var(--accent)] text-xs tracking-widest mb-1 font-mono">// DME PLAYER VALUES</div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tight" data-testid="dme-values-heading">DME Player Values</h1>
        <p className="text-[var(--text-2)] mt-2 max-w-2xl">
          Every player ranked by our proprietary <span className="inline-flex items-center gap-1 text-[var(--accent)]">Dynasty Metrics <DmeTag /></span> value,
          shown against <span className="inline-flex items-center gap-1" style={{ color: "#EF4444" }}>Crowd Sentiment <SentimentTag /></span> and{" "}
          <span className="inline-flex items-center gap-1" style={{ color: "#9CA3AF" }}>Observed Real Trades <RealTradesTag /></span>.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-mono text-[var(--text-3)] tracking-wider border-y border-[var(--border)] py-2.5" data-testid="dme-trust-strip">
        <span className="text-[var(--accent)]">WHY DME ▸</span>
        <span className="inline-flex items-center gap-1.5"><Activity className="w-3.5 h-3.5 text-[var(--accent)]" /> dual-market blend</span>
        <span className="inline-flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-[var(--accent)]" /> dynasty age curves</span>
        <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[var(--accent)]" /> hype-resistant</span>
        <span className="inline-flex items-center gap-1.5"><Cpu className="w-3.5 h-3.5 text-[var(--accent)]" /> re-priced daily</span>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
          <input
            data-testid="dme-values-search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search a player…"
            className="input-dark w-full !pl-10 pr-9"
          />
          {query && (
            <button onClick={() => setQuery("")} data-testid="dme-values-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-3)] hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 border border-[var(--border)] rounded-sm p-0.5" data-testid="dme-values-format">
          {FORMATS.map(f => (
            <button key={f.id} onClick={() => setFmt(f.id)} data-testid={`dme-fmt-${f.id}`}
              className={`text-xs px-3 py-1.5 rounded-sm transition-colors ${fmt === f.id ? "bg-[var(--accent)] text-black font-semibold" : "text-[var(--text-2)] hover:text-white"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {POSITIONS.map(p => (
          <button key={p} onClick={() => setPos(p)} data-testid={`dme-pos-${p}`}
            className={`text-xs px-3 py-1 rounded-sm border transition-colors ${pos === p ? "bg-[var(--accent)] text-black font-semibold border-[var(--accent)]" : "text-[var(--text-2)] border-[var(--border)] hover:text-white"}`}>
            {p}
          </button>
        ))}
        <span className="ml-auto text-[10px] tracking-widest text-[var(--text-3)] font-mono">{rows.length} PLAYERS</span>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-[var(--text-2)]" data-testid="dme-badge-legend">
        <span className="text-[10px] tracking-widest text-[var(--text-3)] font-mono">POSITIONAL TIERS · TRADE TAX:</span>
        <span className="inline-flex items-center gap-1.5"><PlayerBadge badge={{ tier: "xfactor", position: "", pos_rank: 1, tax: 0.2 }} size={15} /> X-Factor (top 5) · +20%</span>
        <span className="inline-flex items-center gap-1.5"><PlayerBadge badge={{ tier: "gold", position: "", pos_rank: 6, tax: 0.15 }} size={15} /> Superstar (6-10) · +15%</span>
        <span className="inline-flex items-center gap-1.5"><PlayerBadge badge={{ tier: "silver", position: "", pos_rank: 11, tax: 0.1 }} size={15} /> Star (11-15) · +10%</span>
      </div>

      {loading && <div className="card-bordered p-12 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>}

      {!loading && rows.length === 0 && (
        <div className="card-bordered p-10 text-center text-[var(--text-2)]" data-testid="dme-values-empty">No players match your search.</div>
      )}

      {!loading && rows.length > 0 && (
        <div className="card-bordered overflow-hidden" data-testid="dme-values-table">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] tracking-[0.15em] text-[var(--text-3)] font-mono border-b border-[var(--border)]">
            <div className="col-span-1">#</div>
            <div className="col-span-5">PLAYER</div>
            <div className="col-span-2 text-right"><span className="inline-flex items-center gap-1 justify-end text-[var(--accent)]">DME <DmeTag /></span></div>
            <div className="col-span-2 text-right"><span className="inline-flex items-center gap-1 justify-end" style={{ color: "#EF4444" }}>SENTIMENT <SentimentTag /></span></div>
            <div className="col-span-2 text-right"><span className="inline-flex items-center gap-1 justify-end" style={{ color: "#9CA3AF" }}>REAL TRADES <RealTradesTag /></span></div>
          </div>
          {rows.map(p => (
            <div
              key={p.player_id}
              onClick={() => nav(`/app/players/${p.player_id}`)}
              className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center text-sm border-b border-[var(--border)] last:border-0 hover:bg-white/5 cursor-pointer"
              data-testid={`dme-row-${p.player_id}`}
            >
              <div className="col-span-1 font-mono text-[var(--text-3)]">{p.rank}</div>
              <div className="col-span-5 min-w-0 flex items-center gap-2.5">
                <PlayerHeadshot playerId={p.player_id} name={p.name} team={p.team} size={36} />
                <div className="min-w-0">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <Link to={`/app/players/${p.player_id}`} onClick={e => e.stopPropagation()} className="hover:text-[var(--accent)] truncate">{p.name}</Link>
                    <PlayerBadge badge={p.badge} />
                  </span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <PosRank rank={p.pos_rank} delta={p.pos_rank_delta} onFilter={setPos} />
                    <TeamPill team={p.team} />
                    <span className="text-[10px] tracking-widest text-[var(--text-3)]">{p.position}{p.age ? ` · ${p.age}y` : ""}</span>
                  </div>
                </div>
              </div>
              <div className="col-span-2 text-right font-mono text-[var(--accent)]" data-testid={`dme-val-${p.player_id}`}>{formatValue(p.dme)}</div>
              <div className="col-span-2 text-right font-mono" style={{ color: "#EF4444" }}>{p.sentiment ? formatValue(p.sentiment) : "—"}</div>
              <div className="col-span-2 text-right font-mono" style={{ color: "#9CA3AF" }}>{p.trades ? formatValue(p.trades) : "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
